"use strict";
import { useTranslation } from "react-i18next";
import { Flex, Text, TouchableArea, useIsDarkMode, useSporeColors } from "ui/src";
import { AlertTriangleFilled } from "ui/src/components/icons/AlertTriangleFilled";
import { RotatableChevron } from "ui/src/components/icons/RotatableChevron";
import { opacify } from "ui/src/theme";
export const SHADOW_OFFSET_SMALL = { width: 0, height: 2 };
export function Shadow({ children, ...rest }) {
  const isDarkMode = useIsDarkMode();
  const colors = useSporeColors();
  const hasBackgroundColor = Boolean(rest.backgroundColor);
  return <Flex
    borderRadius="$rounded16"
    p="$spacing12"
    shadowColor={isDarkMode ? "$black" : "$transparent"}
    shadowOffset={SHADOW_OFFSET_SMALL}
    shadowOpacity={75e-4}
    shadowRadius={10}
    style={hasBackgroundColor ? void 0 : { backgroundColor: opacify(isDarkMode ? 10 : 100, colors.white.val) }}
    {...rest}
  >
      {children}
    </Flex>;
}
function Header({ title, subtitle, onPress, icon, ...buttonProps }) {
  return <TouchableArea
    borderBottomColor="$surface3"
    borderBottomWidth={0.25}
    px="$spacing16"
    py="$spacing12"
    onPress={onPress}
    {...buttonProps}
  >
      <Flex row alignItems="center" gap="$spacing16" justifyContent="space-between">
        <Flex gap="$spacing4">
          <Flex row alignItems="center" gap="$spacing8">
            {icon}
            {typeof title === "string" ? <Text color="$neutral2" variant="subheading2">
                {title}
              </Text> : title}
          </Flex>
          {subtitle ? typeof subtitle === "string" ? <Text variant="subheading1">{subtitle}</Text> : subtitle : null}
        </Flex>
        <RotatableChevron color="$neutral2" direction="end" height={20} />
      </Flex>
    </TouchableArea>;
}
function EmptyState({
  additionalButtonLabel,
  buttonLabel,
  description,
  onPress,
  onPressAdditional,
  title,
  icon
}) {
  return <Flex centered gap="$spacing16" width="100%">
      <Flex centered gap="$spacing8">
        {icon}
        <Flex centered gap="$spacing8">
          {title && <Text textAlign="center" variant="buttonLabel2">
              {title}
            </Text>}
          <Text color="$neutral2" textAlign="center" variant="body2">
            {description}
          </Text>
        </Flex>
      </Flex>
      <Flex row gap="$spacing16">
        {buttonLabel && <TouchableArea onPress={onPress}>
            <Text color="$neutral2Temp" textAlign="center" variant="buttonLabel2">
              {buttonLabel}
            </Text>
          </TouchableArea>}
        {additionalButtonLabel && <TouchableArea onPress={onPressAdditional}>
            <Text color="$neutral2Temp" variant="buttonLabel2">
              {additionalButtonLabel}
            </Text>
          </TouchableArea>}
      </Flex>
    </Flex>;
}
function ErrorState(props) {
  const { t } = useTranslation();
  const { title, description = t("common.card.error.description"), retryButtonLabel, onRetry, icon } = props;
  return <Flex centered grow gap="$spacing24" p="$spacing12" width="100%">
      <Flex centered gap="$spacing16">
        {icon}
        <Flex centered gap="$spacing8">
          {title ? <Text textAlign="center" variant="buttonLabel2">
              {title}
            </Text> : null}
          <Text color="$neutral2" textAlign="center" variant="body2">
            {description}
          </Text>
        </Flex>
      </Flex>
      <Flex row>
        {retryButtonLabel ? <TouchableArea onPress={onRetry}>
            <Text color="$neutral2Temp" variant="buttonLabel2">
              {retryButtonLabel}
            </Text>
          </TouchableArea> : null}
      </Flex>
    </Flex>;
}
function InlineErrorState(props) {
  const { t } = useTranslation();
  const {
    backgroundColor = "$surface2",
    textColor = "$neutral1",
    title = t("common.card.error.title"),
    onRetry: retry,
    retryButtonLabel = t("common.button.retry"),
    icon = <AlertTriangleFilled color="$neutral3" size="$icon.16" testID="error-icon" />
  } = props;
  return <Flex
    grow
    row
    alignItems="center"
    backgroundColor={backgroundColor}
    borderRadius="$rounded16"
    gap="$spacing24"
    justifyContent="space-between"
    p="$spacing12"
    width="100%"
  >
      <Flex row shrink alignItems="center" gap="$spacing8">
        {icon}
        <Text color={textColor} ellipsizeMode="tail" numberOfLines={1} textAlign="center" variant="subheading2">
          {title}
        </Text>
      </Flex>
      {retry ? <TouchableArea onPress={retry}>
          <Text color="$neutral2Temp" variant="buttonLabel2">
            {retryButtonLabel}
          </Text>
        </TouchableArea> : null}
    </Flex>;
}
export const BaseCard = {
  EmptyState,
  ErrorState,
  Header,
  InlineErrorState,
  Shadow
};
